import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    ArrowDown,
    ArrowUp,
    Bell,
    Building2,
    Check,
    ChevronDown,
    Home,
    Loader2,
    MapPin,
    Mic,
    RefreshCw,
    Save,
    Search,
    ShoppingBag,
    User,
} from "lucide-react";
import { CatalogService } from "../../../services/catalog.service";
import { warehouseService } from "../../../services/warehouse.service";
import { getAuthUser, hasPermission } from "../../AdminAuth/components/authStore";

function normalizeList(json) {
    if (Array.isArray(json)) return json;
    if (Array.isArray(json?.items)) return json.items;
    if (Array.isArray(json?.data)) return json.data;
    if (Array.isArray(json?.data?.items)) return json.data.items;
    if (Array.isArray(json?.data?.rows)) return json.data.rows;
    if (Array.isArray(json?.rows)) return json.rows;
    if (Array.isArray(json?.warehouses)) return json.warehouses;
    return [];
}

function imgUrl(url) {
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    const base = String(process.env.REACT_APP_CATALOG_BASE_URL || "").replace(/\/$/, "");
    return base ? `${base}${url.startsWith("/") ? "" : "/"}${url}` : url;
}

function normalizeCategory(item = {}) {
    return {
        ...item,
        parent_id: item?.parent_id ?? item?.parentId ?? null,
        image_url: item?.image_url ?? item?.imageUrl ?? "",
        sort_order: Number(item?.sort_order ?? item?.sortOrder ?? 0),
    };
}

function normalizeWarehouse(item = {}) {
    return {
        ...item,
        id: item?.id ?? item?.warehouse_id ?? item?.warehouseId ?? "",
        name: item?.name ?? item?.warehouse_name ?? item?.warehouseName ?? item?.title ?? "Warehouse",
        city: item?.city ?? item?.address?.city ?? item?.location?.city ?? "",
        state: item?.state ?? item?.address?.state ?? item?.location?.state ?? "",
        status: item?.status ?? item?.is_active ?? item?.active ?? "",
    };
}

const ROOT_PARENT_KEY = "__root__";

function getParentKey(item = {}) {
    const parentId = item?.parent_id ?? item?.parentId ?? null;
    return parentId ? String(parentId) : ROOT_PARENT_KEY;
}

function sortCategorySiblings(items = []) {
    return [...items].sort((a, b) => {
        const sortDiff = Number(a?.sort_order || 0) - Number(b?.sort_order || 0);
        if (sortDiff !== 0) return sortDiff;
        return String(a?.name || "").localeCompare(String(b?.name || ""));
    });
}

function buildSiblingPositionMap(items = []) {
    const groups = new Map();

    items.forEach((item) => {
        if (!item?.id) return;
        const key = getParentKey(item);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
    });

    const map = new Map();

    groups.forEach((siblings) => {
        sortCategorySiblings(siblings).forEach((item, index) => {
            map.set(String(item.id), index + 1);
        });
    });

    return map;
}

function getChangedCategoryIds(nextCategories = [], originalMap = new Map()) {
    const nextMap = buildSiblingPositionMap(nextCategories);
    const changed = new Set();

    nextMap.forEach((position, id) => {
        if (originalMap.get(String(id)) !== position) {
            changed.add(String(id));
        }
    });

    return changed;
}

function buildReorderPayloadItems(items = []) {
    const orderMap = buildSiblingPositionMap(items);

    return items
        .filter((item) => item?.id)
        .map((item) => ({
            id: item.id,
            sortOrder: orderMap.get(String(item.id)) || Number(item.sort_order || item.sortOrder || 0),
            isVisible: item.isVisibleInWarehouse ?? item.is_visible ?? true,
        }));
}

const DEFAULT_LAYOUT_ID = "__default_order__";

export default function AppLayoutManager() {
    const user = getAuthUser() || {};
    const isSuperAdmin = String(user?.role || "").toLowerCase() === "super_admin";
    const canManageAppLayout =
        isSuperAdmin || hasPermission(["admin.app_layout.manage"]);

    const [warehouses, setWarehouses] = useState([]);
    const [warehouseLoading, setWarehouseLoading] = useState(true);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState(DEFAULT_LAYOUT_ID);
    const [warehouseError, setWarehouseError] = useState("");

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [msg, setMsg] = useState("");
    const [successPopup, setSuccessPopup] = useState(false);
    const [changedCategoryIds, setChangedCategoryIds] = useState(new Set());
    const [originalOrderMap, setOriginalOrderMap] = useState(new Map());

    const layoutDropdownRef = useRef(null);
    const previewAnchorRef = useRef(null);
    const [layoutDropdownOpen, setLayoutDropdownOpen] = useState(false);
    const [previewPinned, setPreviewPinned] = useState(false);
    const [collapsedParentIds, setCollapsedParentIds] = useState(new Set());

    useEffect(() => {
        const updatePreviewPin = () => {
            const anchor = previewAnchorRef.current;
            if (!anchor || window.innerWidth < 1280) {
                setPreviewPinned(false);
                return;
            }

            const anchorTop = anchor.getBoundingClientRect().top;
            setPreviewPinned(anchorTop <= 104);
        };

        updatePreviewPin();
        window.addEventListener("scroll", updatePreviewPin, { passive: true });
        window.addEventListener("resize", updatePreviewPin);

        return () => {
            window.removeEventListener("scroll", updatePreviewPin);
            window.removeEventListener("resize", updatePreviewPin);
        };
    }, []);

    const toggleSubcategoryPanel = (parentId) => {
        const key = String(parentId);
        setCollapsedParentIds((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const selectedWarehouse = useMemo(() => {
        return warehouses.find((x) => String(x.id) === String(selectedWarehouseId)) || null;
    }, [warehouses, selectedWarehouseId]);

    const isDefaultLayout = selectedWarehouseId === DEFAULT_LAYOUT_ID;

    const selectedLayoutName = isDefaultLayout
        ? "Default Order"
        : selectedWarehouse?.name || "selected warehouse";

    const layoutOptions = useMemo(() => {
        const defaultOption = {
            id: DEFAULT_LAYOUT_ID,
            name: "Default Order",
            meta: "Fallback category order",
        };

        return [
            defaultOption,
            ...warehouses.map((w) => ({
                id: w.id,
                name: w.name,
                meta: [w.city, w.state].filter(Boolean).join(", ") || "Warehouse layout",
            })),
        ];
    }, [warehouses]);

    const selectedLayoutOption = useMemo(() => {
        return (
            layoutOptions.find((item) => String(item.id) === String(selectedWarehouseId)) ||
            layoutOptions[0] ||
            { id: DEFAULT_LAYOUT_ID, name: "Default Order", meta: "Fallback category order" }
        );
    }, [layoutOptions, selectedWarehouseId]);

    const mainCategories = useMemo(() => {
        return [...categories]
            .filter((x) => !x.parent_id)
            .sort((a, b) => {
                const s = Number(a.sort_order || 0) - Number(b.sort_order || 0);
                return s || String(a.name || "").localeCompare(String(b.name || ""));
            });
    }, [categories]);

    const subByParent = useMemo(() => {
        const map = {};
        categories.forEach((c) => {
            if (!c.parent_id) return;
            const key = String(c.parent_id);
            if (!map[key]) map[key] = [];
            map[key].push(c);
        });

        Object.keys(map).forEach((key) => {
            map[key].sort((a, b) => {
                const s = Number(a.sort_order || 0) - Number(b.sort_order || 0);
                return s || String(a.name || "").localeCompare(String(b.name || ""));
            });
        });

        return map;
    }, [categories]);

    async function loadWarehouses() {
        try {
            setWarehouseLoading(true);
            setWarehouseError("");
            const res = await warehouseService.listWarehouses({ page: 1, limit: 200 });
            const list = normalizeList(res?.data || res).map(normalizeWarehouse).filter((x) => x.id);
            setWarehouses(list);
            setSelectedWarehouseId((prev) => prev || DEFAULT_LAYOUT_ID);
        } catch (e) {
            setWarehouseError(e?.response?.data?.message || e.message || "Failed to load warehouses");
        } finally {
            setWarehouseLoading(false);
        }
    }

    async function loadCategories(warehouseId = selectedWarehouseId) {
        if (!warehouseId) {
            setCategories([]);
            setOriginalOrderMap(new Map());
            setDirty(false);
            setChangedCategoryIds(new Set());
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setMsg("");

            const isDefault = warehouseId === DEFAULT_LAYOUT_ID;

            const res = await CatalogService.getCategories(
                isDefault
                    ? {
                        assignedOnly: false,
                    }
                    : {
                        warehouseId,
                        assignedOnly: true,
                    }
            );

            const normalized = normalizeList(res?.data || res).map(normalizeCategory);

            setCategories(normalized);

            setOriginalOrderMap(buildSiblingPositionMap(normalized));

            setDirty(false);
            setChangedCategoryIds(new Set());
        } catch (e) {
            setMsg(e?.response?.data?.message || e.message || "Failed to load categories");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadWarehouses();
    }, []);

    useEffect(() => {
        if (!layoutDropdownOpen) return undefined;

        function handleOutsideClick(event) {
            if (layoutDropdownRef.current && !layoutDropdownRef.current.contains(event.target)) {
                setLayoutDropdownOpen(false);
            }
        }

        function handleEscape(event) {
            if (event.key === "Escape") setLayoutDropdownOpen(false);
        }

        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [layoutDropdownOpen]);

    useEffect(() => {
        if (!warehouseLoading && selectedWarehouseId) {
            loadCategories(selectedWarehouseId);
        }
        if (!warehouseLoading && !selectedWarehouseId) {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWarehouseId, warehouseLoading]);

    function handleWarehouseChange(nextWarehouseId) {
        if (dirty) {
            const ok = window.confirm("You have unsaved layout changes. Switch warehouse without saving?");
            if (!ok) return;
        }
        setSelectedWarehouseId(nextWarehouseId);
        setLayoutDropdownOpen(false);
        setMsg("");
        setDirty(false);
        setChangedCategoryIds(new Set());
    }

    function moveCategory(item, dir) {
        if (!canManageAppLayout) return;

        const parentKey = getParentKey(item);
        const siblings = sortCategorySiblings(
            categories.filter((x) => getParentKey(x) === parentKey)
        );

        const index = siblings.findIndex((x) => String(x.id) === String(item.id));
        const next = index + dir;
        if (index < 0 || next < 0 || next >= siblings.length) return;

        const orderedSiblings = [...siblings];
        const [picked] = orderedSiblings.splice(index, 1);
        orderedSiblings.splice(next, 0, picked);

        const orderMap = new Map(
            orderedSiblings.map((x, i) => [String(x.id), i + 1])
        );

        const nextCategories = categories.map((x) => {
            const nextOrder = orderMap.get(String(x.id));
            if (!nextOrder) return x;

            return {
                ...x,
                sort_order: nextOrder,
                sortOrder: nextOrder,
            };
        });

        const nextChangedIds = getChangedCategoryIds(nextCategories, originalOrderMap);

        setCategories(nextCategories);
        setChangedCategoryIds(nextChangedIds);
        setDirty(nextChangedIds.size > 0);
        setMsg("");
    }

    function showNoPermissionPopup() {
        const text = "You do not have permission to update App Layout. Please ask admin to assign Manage App Layout permission.";
        setMsg(text);
        window.alert(text);
    }

    async function saveOrder() {
        if (!canManageAppLayout) {
            showNoPermissionPopup();
            return;
        }

        if (!selectedWarehouseId) {
            setMsg("Please select a warehouse first.");
            return;
        }

        try {
            setSaving(true);
            setMsg("");

            const payload = {
                items: buildReorderPayloadItems(categories),
            };

            if (!isDefaultLayout) {
                payload.warehouseId = selectedWarehouseId;
            }

            await CatalogService.reorderCategories(payload);

            setDirty(false);
            setMsg("");
            setSuccessPopup(true);
            await loadCategories(selectedWarehouseId);
        } catch (e) {
            if (e?.response?.status === 403) {
                showNoPermissionPopup();
            } else {
                setMsg(e?.response?.data?.message || e.message || "Failed to save order");
            }
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="w-full max-w-full overflow-visible pb-4">
            {successPopup ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-sm">
                    <div className="w-full max-w-[430px] rounded-[28px] border border-white/70 bg-white p-7 text-center shadow-[0_28px_80px_rgba(15,23,42,0.28)]">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[22px] bg-gradient-to-br from-emerald-100 to-lime-100 text-2xl text-emerald-600 shadow-inner">
                            ✓
                        </div>

                        <h3 className="mt-5 text-[22px] font-black tracking-tight text-slate-900">
                            Layout saved!
                        </h3>

                        <p className="mt-2 text-[13px] font-medium leading-5 text-slate-500">
                            Category and subcategory order has been updated for {selectedLayoutName}.
                        </p>

                        <button
                            type="button"
                            onClick={() => setSuccessPopup(false)}
                            className="mt-6 h-[44px] w-full rounded-[14px] bg-gradient-to-r from-[#d86a78] to-[#ef8090] text-[13px] font-bold text-white shadow-[0_12px_28px_rgba(216,106,120,0.24)] transition hover:-translate-y-0.5"
                        >
                            Nice, got it
                        </button>
                    </div>
                </div>
            ) : null}

            <section className="relative z-30 overflow-visible rounded-[30px] border border-[#f4cbd1] bg-[linear-gradient(125deg,#fff1f4_0%,#fff9ed_48%,#f2f6ff_100%)] p-5 shadow-[0_20px_55px_rgba(216,106,120,0.10)] sm:p-7">
                <div className="pointer-events-none absolute -right-14 -top-16 h-52 w-52 rounded-full bg-[#ffb7c1]/25 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-20 left-[36%] h-48 w-48 rounded-full bg-[#ffd787]/30 blur-3xl" />
                <div className="relative grid gap-6 xl:grid-cols-[1.08fr_0.92fr] xl:items-end">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/80 bg-white/75 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-[#d86a78] shadow-sm backdrop-blur">
                            <span className="text-[15px]">✦</span>
                            App layout studio
                        </div>
                        <h1 className="mt-4 max-w-2xl text-[28px] font-black leading-[1.08] tracking-[-0.035em] text-slate-900 sm:text-[34px]">
                            Shape what shoppers
                            <span className="block bg-gradient-to-r from-[#d86a78] via-[#ef8a73] to-[#d99b3d] bg-clip-text text-transparent">
                                see first.
                            </span>
                        </h1>
                        <p className="mt-3 max-w-2xl text-[13px] font-medium leading-6 text-slate-600">
                            Reorder main categories and subcategories for the default experience or for a specific warehouse. Your right-side phone preview updates with the same order.
                        </p>

                        <div className="mt-5 grid max-w-[680px] grid-cols-3 gap-2 sm:gap-3">
                            <div className="rounded-[18px] border border-white/80 bg-white/80 px-3 py-3 shadow-sm backdrop-blur">
                                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Main categories</div>
                                <div className="mt-1 text-[20px] font-black text-slate-900">{mainCategories.length}</div>
                            </div>
                            <div className="rounded-[18px] border border-white/80 bg-white/80 px-3 py-3 shadow-sm backdrop-blur">
                                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Layouts</div>
                                <div className="mt-1 text-[20px] font-black text-slate-900">{layoutOptions.length}</div>
                            </div>
                            <div className="rounded-[18px] border border-white/80 bg-white/80 px-3 py-3 shadow-sm backdrop-blur">
                                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Unsaved moves</div>
                                <div className={`mt-1 text-[20px] font-black ${dirty ? "text-[#d86a78]" : "text-emerald-600"}`}>
                                    {dirty ? changedCategoryIds.size : 0}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="relative z-40 rounded-[24px] border border-white/90 bg-white/90 p-4 shadow-[0_14px_34px_rgba(15,23,42,0.08)] backdrop-blur-sm">
                        <div className="mb-2 flex items-center justify-between gap-3">
                            <div>
                                <div className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">Layout target</div>
                                <div className="mt-1 text-[14px] font-bold text-slate-800">Choose where this order appears</div>
                            </div>
                            <span className={`rounded-full px-2.5 py-1 text-[10px] font-black ${dirty ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                                {dirty ? "UNSAVED" : "SYNCED"}
                            </span>
                        </div>

                        <div className="relative" ref={layoutDropdownRef}>
                            <button
                                type="button"
                                disabled={warehouseLoading || saving}
                                onClick={() => setLayoutDropdownOpen((current) => !current)}
                                aria-haspopup="listbox"
                                aria-expanded={layoutDropdownOpen}
                                className={`flex min-h-[54px] w-full items-center gap-3 rounded-[16px] border bg-white px-3.5 text-left outline-none transition ${layoutDropdownOpen
                                    ? "border-[#d86a78] shadow-[0_12px_28px_rgba(216,106,120,0.16)] ring-4 ring-[#fff1f3]"
                                    : "border-[#f1ccd1] hover:border-[#d86a78]"
                                    } disabled:cursor-not-allowed disabled:opacity-60`}
                            >
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] bg-gradient-to-br from-[#fff0f2] to-[#ffe0e5] text-[#d86a78]">
                                    <Building2 size={17} />
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-[13px] font-bold text-slate-900">
                                        {warehouseLoading ? "Loading warehouses..." : selectedLayoutOption.name}
                                    </span>
                                    <span className="mt-0.5 block truncate text-[11px] font-semibold text-slate-400">
                                        {selectedLayoutOption.meta}
                                    </span>
                                </span>
                                <ChevronDown
                                    size={17}
                                    className={`shrink-0 text-[#d86a78] transition-transform ${layoutDropdownOpen ? "rotate-180" : ""}`}
                                />
                            </button>

                            {layoutDropdownOpen ? (
                                <div
                                    role="listbox"
                                    className="absolute left-0 top-[calc(100%+8px)] z-[200] w-full overflow-hidden rounded-[20px] border border-[#f2c6cc] bg-white shadow-[0_22px_55px_rgba(15,23,42,0.18)]"
                                >
                                    <div className="max-h-[min(360px,52vh)] overflow-y-auto overscroll-contain p-2">
                                        {warehouseLoading ? (
                                            <div className="flex items-center gap-2 rounded-xl px-3 py-3 text-sm font-medium text-slate-500">
                                                <Loader2 size={16} className="animate-spin text-[#d86a78]" />
                                                Loading warehouses...
                                            </div>
                                        ) : null}

                                        {!warehouseLoading && !layoutOptions.length ? (
                                            <div className="rounded-xl px-3 py-3 text-sm font-medium text-slate-500">
                                                No layout found
                                            </div>
                                        ) : null}

                                        {!warehouseLoading
                                            ? layoutOptions.map((item) => {
                                                const active = String(item.id) === String(selectedWarehouseId);
                                                return (
                                                    <button
                                                        key={item.id}
                                                        type="button"
                                                        role="option"
                                                        aria-selected={active}
                                                        onClick={() => handleWarehouseChange(item.id)}
                                                        className={`flex w-full items-center gap-3 rounded-[14px] px-3 py-2.5 text-left transition ${active
                                                            ? "bg-gradient-to-r from-[#d86a78] to-[#ec7d8c] text-white shadow-sm"
                                                            : "text-slate-700 hover:bg-[#fff1f3] hover:text-[#d86a78]"
                                                            }`}
                                                    >
                                                        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] ${active ? "bg-white/20" : "bg-[#fff1f3] text-[#d86a78]"}`}>
                                                            <Building2 size={15} />
                                                        </span>
                                                        <span className="min-w-0 flex-1">
                                                            <span className="block truncate text-sm font-bold">{item.name}</span>
                                                            <span className={`block truncate text-[11px] font-semibold ${active ? "text-white/80" : "text-slate-400"}`}>
                                                                {item.meta}
                                                            </span>
                                                        </span>
                                                        {active ? <Check size={16} className="shrink-0" /> : null}
                                                    </button>
                                                );
                                            })
                                            : null}
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-2">
                            <button
                                onClick={() => loadCategories(selectedWarehouseId)}
                                disabled={loading || saving || !selectedWarehouseId}
                                className="inline-flex h-[42px] items-center justify-center gap-2 rounded-[13px] border border-[#f2c6cc] bg-white px-3 text-[12px] font-bold text-[#d86a78] transition hover:bg-[#fff6f7] disabled:opacity-50"
                            >
                                <RefreshCw size={15} />
                                Refresh
                            </button>
                            <button
                                onClick={saveOrder}
                                disabled={!canManageAppLayout || !dirty || saving || loading || !selectedWarehouseId}
                                title={!canManageAppLayout ? "Manage App Layout permission required" : ""}
                                className="inline-flex h-[42px] items-center justify-center gap-2 rounded-[13px] bg-gradient-to-r from-[#d86a78] to-[#ee7d8c] px-3 text-[12px] font-bold text-white shadow-[0_10px_24px_rgba(216,106,120,0.2)] transition hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-45"
                            >
                                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                                Save Layout
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {warehouseError ? (
                <div className="mt-4 rounded-[16px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700">
                    {warehouseError}
                </div>
            ) : null}

            {msg ? (
                <div className="mt-4 rounded-[16px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-3 text-sm font-semibold text-[#d86a78]">
                    {msg}
                </div>
            ) : null}

            {selectedWarehouse || isDefaultLayout ? (
                <div className="relative z-10 mt-5 flex flex-col gap-3 rounded-[22px] border border-[#f2c6cc] bg-white px-5 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[15px] bg-[#fff1f3] text-[#d86a78]">
                            <Building2 size={19} />
                        </div>
                        <div className="min-w-0">
                            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#d86a78]">Editing now</div>
                            <div className="mt-1 truncate text-[18px] font-black text-slate-900">
                                {selectedLayoutName}
                                {!isDefaultLayout && selectedWarehouse?.city ? (
                                    <span className="font-semibold text-slate-400"> · {selectedWarehouse.city}</span>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[#f7f2ff] px-3 py-1.5 text-[11px] font-bold text-[#7455a6]">
                            {mainCategories.length} categories
                        </span>
                        <span className="rounded-full bg-[#eef8ff] px-3 py-1.5 text-[11px] font-bold text-[#3c80a7]">
                            {isDefaultLayout ? "Default fallback" : "Warehouse specific"}
                        </span>
                    </div>
                </div>
            ) : null}

            <div className="relative z-0 mt-5 grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_390px]">
                <section className="overflow-hidden rounded-[26px] border border-[#f2c6cc] bg-white shadow-[0_16px_40px_rgba(15,23,42,0.06)]">
                    <div className="border-b border-[#f7d9dd] bg-[linear-gradient(110deg,#fff8f9_0%,#fffdf7_100%)] px-5 py-5 sm:px-6">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[20px]">🧩</span>
                                    <h2 className="text-[19px] font-black tracking-tight text-slate-900">Order studio</h2>
                                </div>
                                <p className="mt-1 max-w-2xl text-[12px] font-medium leading-5 text-slate-500">
                                    {isDefaultLayout
                                        ? "Move cards to define the fallback category and subcategory sequence in the user app."
                                        : "Move cards to define category and subcategory sequence for this warehouse only."}
                                </p>
                            </div>
                            <div className="rounded-[14px] bg-slate-900 px-3 py-2 text-[11px] font-bold text-white shadow-sm">
                                ↑ ↓ Use arrows to reorder
                            </div>
                        </div>
                    </div>

                    <div className="p-4 sm:p-5">
                        {!canManageAppLayout ? (
                            <div className="mb-4 rounded-[16px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700">
                                View only access: you can see app layout, but cannot move categories/subcategories or save changes.
                            </div>
                        ) : null}

                        {loading || warehouseLoading ? (
                            <div className="flex h-[420px] items-center justify-center rounded-[20px] bg-slate-50">
                                <div className="text-center">
                                    <Loader2 className="mx-auto animate-spin text-[#d86a78]" size={30} />
                                    <div className="mt-3 text-[12px] font-bold text-slate-400">Building your layout…</div>
                                </div>
                            </div>
                        ) : !selectedWarehouseId ? (
                            <div className="flex h-[300px] items-center justify-center rounded-[20px] border border-dashed border-[#f2c6cc] bg-[#fffafb] text-center">
                                <div>
                                    <div className="text-lg font-bold text-slate-900">Select a warehouse</div>
                                    <div className="mt-1 text-sm font-semibold text-slate-500">Warehouse-wise layout will load here.</div>
                                </div>
                            </div>
                        ) : !mainCategories.length ? (
                            <div className="flex h-[300px] items-center justify-center rounded-[20px] border border-dashed border-[#f2c6cc] bg-[#fffafb] text-center">
                                <div className="px-6">
                                    <div className="text-lg font-bold text-slate-900">{isDefaultLayout ? "No categories found" : "No assigned categories found"}</div>
                                    <div className="mt-1 text-sm font-semibold text-slate-500">
                                        {isDefaultLayout
                                            ? "Add active categories first, then default order will appear here."
                                            : "Assign products to this warehouse first, then categories will appear here."}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {mainCategories.map((item, index) => {
                                    const subcategories = subByParent[String(item.id)] || [];
                                    const hasChangedChild = subcategories.some((sub) => changedCategoryIds.has(String(sub.id)));
                                    const changed = changedCategoryIds.has(String(item.id)) || hasChangedChild;
                                    const subcategoriesCollapsed = collapsedParentIds.has(String(item.id));

                                    return (
                                        <div
                                            key={item.id}
                                            className={`group overflow-hidden rounded-[20px] border transition-all ${changed
                                                ? "border-[#d86a78] bg-[linear-gradient(110deg,#fff2f4_0%,#fffaf2_100%)] shadow-[0_12px_30px_rgba(216,106,120,0.13)] ring-2 ring-[#fbe0e4]"
                                                : "border-[#edf0f4] bg-[#fbfcfe] hover:border-[#f2c6cc] hover:shadow-sm"
                                                }`}
                                        >
                                            <div className="flex items-center gap-3 p-3.5 sm:p-4">
                                                <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-[15px] bg-gradient-to-br from-[#d86a78] to-[#f09b73] text-[15px] font-black text-white shadow-[0_8px_18px_rgba(216,106,120,0.23)]">
                                                    {index + 1}
                                                    <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#ffd064]" />
                                                </div>

                                                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-[16px] border border-white bg-white shadow-sm">
                                                    {item.image_url ? (
                                                        <img src={imgUrl(item.image_url)} alt="" className="h-full w-full object-cover" />
                                                    ) : (
                                                        <div className="flex h-full w-full items-center justify-center bg-[#fff1f3] text-lg font-black text-[#d86a78]">
                                                            {String(item.name || "?").slice(0, 1)}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="min-w-0 flex-1">
                                                    <div className="truncate text-[14px] font-black text-slate-900">{item.name}</div>
                                                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] font-bold">
                                                        <span className="rounded-full bg-white px-2 py-1 text-slate-500 shadow-sm">{subcategories.length} subcategories</span>
                                                        <span className="rounded-full bg-[#eef6ff] px-2 py-1 text-[#4b77a2]">
                                                            {isDefaultLayout ? "Default" : "Assigned"}
                                                        </span>
                                                        {changedCategoryIds.has(String(item.id)) ? (
                                                            <span className="rounded-full bg-[#d86a78] px-2 py-1 text-white">Moved</span>
                                                        ) : null}
                                                        {hasChangedChild ? (
                                                            <span className="rounded-full bg-[#8f6cc8] px-2 py-1 text-white">Child moved</span>
                                                        ) : null}
                                                    </div>
                                                </div>

                                                <div className="flex shrink-0 gap-1.5">
                                                    <button
                                                        onClick={() => moveCategory(item, -1)}
                                                        disabled={!canManageAppLayout || index === 0}
                                                        title={!canManageAppLayout ? "Manage App Layout permission required" : "Move category up"}
                                                        className="grid h-9 w-9 place-items-center rounded-[12px] border border-[#f2c6cc] bg-white text-[#d86a78] shadow-sm transition hover:-translate-y-0.5 hover:bg-[#fff1f3] disabled:translate-y-0 disabled:opacity-35"
                                                    >
                                                        <ArrowUp size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => moveCategory(item, 1)}
                                                        disabled={!canManageAppLayout || index === mainCategories.length - 1}
                                                        title={!canManageAppLayout ? "Manage App Layout permission required" : "Move category down"}
                                                        className="grid h-9 w-9 place-items-center rounded-[12px] border border-[#f2c6cc] bg-white text-[#d86a78] shadow-sm transition hover:translate-y-0.5 hover:bg-[#fff1f3] disabled:translate-y-0 disabled:opacity-35"
                                                    >
                                                        <ArrowDown size={16} />
                                                    </button>
                                                </div>
                                            </div>

                                            {subcategories.length ? (
                                                <div className="border-t border-[#f4e5e8] bg-white/70 p-3 sm:px-4 sm:py-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleSubcategoryPanel(item.id)}
                                                        className="flex w-full items-center justify-between gap-3 rounded-[12px] px-1 py-1.5 text-left transition hover:bg-[#fff7f8]"
                                                        aria-expanded={!subcategoriesCollapsed}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">Subcategory order</span>
                                                            <span className="rounded-full bg-[#f3ecff] px-2 py-0.5 text-[9px] font-black text-[#805fb3]">
                                                                {subcategories.length}
                                                            </span>
                                                        </div>
                                                        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#f2c6cc] bg-white px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.08em] text-[#d86a78] shadow-sm">
                                                            {subcategoriesCollapsed ? "Show" : "Minimize"}
                                                            <ChevronDown
                                                                size={13}
                                                                className={`transition-transform ${subcategoriesCollapsed ? "" : "rotate-180"}`}
                                                            />
                                                        </span>
                                                    </button>

                                                    {!subcategoriesCollapsed ? (
                                                        <div className="mt-2 space-y-2">
                                                            {subcategories.map((sub, subIndex) => (
                                                                <div
                                                                    key={sub.id}
                                                                    className={`flex items-center gap-3 rounded-[14px] border px-3 py-2 transition-all ${changedCategoryIds.has(String(sub.id))
                                                                        ? "border-[#d86a78] bg-[#fff4f6] shadow-sm"
                                                                        : "border-[#edf0f4] bg-[#fbfcfe]"
                                                                        }`}
                                                                >
                                                                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[9px] bg-[#f3ecff] text-[11px] font-black text-[#805fb3]">
                                                                        {subIndex + 1}
                                                                    </div>
                                                                    <div className="h-9 w-9 shrink-0 overflow-hidden rounded-[10px] border border-white bg-white shadow-sm">
                                                                        {sub.image_url ? (
                                                                            <img src={imgUrl(sub.image_url)} alt="" className="h-full w-full object-cover" />
                                                                        ) : (
                                                                            <div className="flex h-full w-full items-center justify-center bg-[#fff1f3] text-[11px] font-black text-[#d86a78]">
                                                                                {String(sub.name || "?").slice(0, 1)}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="min-w-0 flex-1">
                                                                        <div className="truncate text-[12px] font-bold text-slate-800">{sub.name}</div>
                                                                        <div className="truncate text-[10px] font-semibold text-slate-400">Under {item.name}</div>
                                                                    </div>
                                                                    {changedCategoryIds.has(String(sub.id)) ? (
                                                                        <span className="hidden rounded-full bg-[#d86a78] px-2 py-1 text-[9px] font-black text-white sm:inline-flex">MOVED</span>
                                                                    ) : null}
                                                                    <button
                                                                        onClick={() => moveCategory(sub, -1)}
                                                                        disabled={!canManageAppLayout || subIndex === 0}
                                                                        title={!canManageAppLayout ? "Manage App Layout permission required" : "Move subcategory up"}
                                                                        className="grid h-8 w-8 place-items-center rounded-[10px] border border-[#f2c6cc] bg-white text-[#d86a78] disabled:opacity-35"
                                                                    >
                                                                        <ArrowUp size={13} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => moveCategory(sub, 1)}
                                                                        disabled={!canManageAppLayout || subIndex === subcategories.length - 1}
                                                                        title={!canManageAppLayout ? "Manage App Layout permission required" : "Move subcategory down"}
                                                                        className="grid h-8 w-8 place-items-center rounded-[10px] border border-[#f2c6cc] bg-white text-[#d86a78] disabled:opacity-35"
                                                                    >
                                                                        <ArrowDown size={13} />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="mt-2 rounded-[12px] border border-dashed border-[#f2d6da] bg-[#fffafb] px-3 py-2 text-[10px] font-semibold text-slate-400">
                                                            Subcategories minimized — category stays ready for quick reordering.
                                                        </div>
                                                    )}
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </section>

                <div ref={previewAnchorRef} className="hidden min-h-[640px] xl:block">
                    <section
                        className={`flex max-h-[calc(100dvh-120px)] flex-col rounded-[28px] border border-[#f2c6cc] bg-[linear-gradient(155deg,#fff8f9_0%,#fff2e9_52%,#f7f4ff_100%)] p-4 shadow-[0_18px_45px_rgba(216,106,120,0.14)] ${
                            previewPinned
                                ? "fixed right-6 top-[104px] z-[80] w-[370px] 2xl:right-8"
                                : "relative w-full"
                        }`}
                    >
                    <div className="mb-4 flex shrink-0 items-center justify-between gap-3 px-1">
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="relative flex h-2.5 w-2.5">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
                                </span>
                                <h2 className="text-[15px] font-black text-slate-900">Live app preview</h2>
                            </div>
                            <p className="mt-1 text-[11px] font-semibold text-slate-500">{selectedLayoutName}</p>
                        </div>
                        <span className="rounded-full border border-[#f2c6cc] bg-white/80 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.14em] text-[#d86a78] shadow-sm">Live</span>
                    </div>

                    <div className="relative mx-auto flex h-[720px] min-h-0 w-full max-w-[330px] flex-col overflow-hidden rounded-[38px] border-[7px] border-[#eadde0] bg-white shadow-[0_24px_55px_rgba(122,71,81,0.20)] ring-1 ring-white">
                        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-[#fbfbfd]">
                            <div className="bg-white px-4 pt-4">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="text-[18px] font-black tracking-tight text-slate-900">20 min</div>
                                        <div className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-slate-500">
                                            <MapPin size={11} /> {isDefaultLayout ? "Default" : selectedWarehouse?.city || "Warehouse"} · User location
                                        </div>
                                    </div>
                                    <div className="grid h-8 w-8 place-items-center rounded-full bg-slate-100">
                                        <Bell size={15} className="text-slate-600" />
                                    </div>
                                </div>

                                <div className="mt-4 flex items-center gap-2 rounded-[14px] border border-slate-100 bg-slate-50 px-3 py-2.5 shadow-inner">
                                    <Search size={14} className="text-slate-400" />
                                    <span className="flex-1 text-[11px] font-semibold text-slate-400">Search for groceries</span>
                                    <Mic size={14} className="text-slate-400" />
                                </div>

                                <div className="mt-4 flex gap-4 overflow-x-auto pb-3">
                                    <div className="min-w-[42px] text-center">
                                        <div className="mx-auto grid h-8 w-8 place-items-center rounded-[11px] bg-[#fff1f3] text-[#d86a78]">
                                            <Home size={14} />
                                        </div>
                                        <div className="mt-1 text-[8px] font-bold text-slate-700">All</div>
                                    </div>
                                    {mainCategories.map((c) => (
                                        <div key={c.id} className="min-w-[42px] text-center">
                                            <div className="mx-auto h-8 w-8 overflow-hidden rounded-[11px] bg-[#fff1f3]">
                                                {c.image_url ? <img src={imgUrl(c.image_url)} alt="" className="h-full w-full object-cover" /> : null}
                                            </div>
                                            <div className="mt-1 line-clamp-1 text-[8px] font-bold text-slate-700">{c.name}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mx-3 mt-3 overflow-hidden rounded-[20px] bg-[linear-gradient(120deg,#ffdd8b_0%,#ffc1cb_55%,#cfc8ff_100%)] p-4 shadow-sm">
                                <div className="inline-flex rounded-full bg-white/65 px-2 py-1 text-[8px] font-black uppercase tracking-[0.12em] text-slate-700">Weekend drop</div>
                                <div className="mt-2 text-[17px] font-black leading-5 text-slate-900">Fresh picks.<br />Funky prices.</div>
                                <div className="mt-1 text-[10px] font-semibold text-slate-600">Up to 15% off selected essentials</div>
                                <button className="mt-3 rounded-full bg-gradient-to-r from-[#d86a78] to-[#ef8b78] px-4 py-2 text-[9px] font-bold text-white shadow-sm">Explore now</button>
                            </div>

                            <div className="mt-5 px-4">
                                <div className="mb-3 flex items-center justify-between">
                                    <h3 className="text-[13px] font-black text-slate-900">Shop by category</h3>
                                    <span className="text-[9px] font-bold text-[#d86a78]">View all</span>
                                </div>
                                <div className="grid grid-cols-3 gap-2.5">
                                    {mainCategories.map((c, idx) => (
                                        <div key={c.id} className={`rounded-[15px] p-2 text-center ${idx % 3 === 0 ? "bg-[#fff0f3]" : idx % 3 === 1 ? "bg-[#f0f6ff]" : "bg-[#fff8df]"}`}>
                                            <div className="mx-auto h-12 w-12 overflow-hidden rounded-[13px] bg-white shadow-sm">
                                                {c.image_url ? <img src={imgUrl(c.image_url)} alt="" className="h-full w-full object-cover" /> : null}
                                            </div>
                                            <div className="mt-2 line-clamp-2 text-[9px] font-bold text-slate-800">{c.name}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-5 px-4">
                                <div className="mb-3 flex items-center justify-between">
                                    <h3 className="text-[13px] font-black text-slate-900">Explore more</h3>
                                    <span className="text-[9px] font-bold text-slate-400">For you</span>
                                </div>
                                {mainCategories.map((main) => {
                                    const subs = subByParent[String(main.id)] || [];
                                    if (!subs.length) return null;
                                    return (
                                        <div key={main.id} className="mb-4">
                                            <div className="mb-2 text-[11px] font-black text-slate-800">{main.name}</div>
                                            <div className="grid grid-cols-3 gap-2.5">
                                                {subs.map((s) => (
                                                    <div key={s.id} className="rounded-[14px] border border-slate-100 bg-white p-2 text-center shadow-sm">
                                                        <div className="mx-auto h-11 w-11 overflow-hidden rounded-[12px] bg-slate-50">
                                                            {s.image_url ? <img src={imgUrl(s.image_url)} alt="" className="h-full w-full object-cover" /> : null}
                                                        </div>
                                                        <div className="mt-1.5 line-clamp-2 text-[8px] font-bold text-slate-700">{s.name}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="mx-4 mb-5 rounded-[18px] border border-[#f2d7dc] bg-[linear-gradient(120deg,#fff0f3_0%,#fff6df_55%,#eee9ff_100%)] p-4 text-slate-900 shadow-sm">
                                <div className="text-[13px] font-black">Small basket, big mood ✦</div>
                                <div className="mt-1 text-[9px] font-semibold text-slate-500">Discover quick essentials picked for you</div>
                                <button className="mt-3 rounded-full bg-[#d86a78] px-4 py-2 text-[9px] font-black text-white shadow-sm">Shop now</button>
                            </div>
                        </div>

                        <div className="grid shrink-0 grid-cols-4 border-t border-slate-100 bg-white/95 px-3 py-2.5 backdrop-blur">
                            <div className="text-center text-[#d86a78]">
                                <Home size={17} className="mx-auto" />
                                <div className="text-[8px] font-black">Home</div>
                            </div>
                            <div className="text-center text-slate-300">
                                <Search size={17} className="mx-auto" />
                                <div className="text-[8px] font-bold">Search</div>
                            </div>
                            <div className="text-center text-slate-300">
                                <ShoppingBag size={17} className="mx-auto" />
                                <div className="text-[8px] font-bold">Cart</div>
                            </div>
                            <div className="text-center text-slate-300">
                                <User size={17} className="mx-auto" />
                                <div className="text-[8px] font-bold">Profile</div>
                            </div>
                        </div>
                    </div>
                </section>
                </div>
            </div>
        </div>
    );
}
